/**
 * Top-of-page progress bar (v1.1 C4).
 *
 * Renders a 4 px tall full-width bar at the very top of the viewport
 * whenever any operation is in flight (tracked in
 * `useProgressStore`). Visual states:
 *   • Active: indeterminate animation (looping gradient) in the
 *     primary accent color. We don't try to model "true progress"
 *     because the only multi-row operation we have today (Excel
 *     import) is a single API call without streaming feedback.
 *   • Completion linger: when activeOps drops to 0, the bar holds
 *     a 100 %-filled solid-green state for 500 ms then fades out
 *     over 200 ms. That linger is the "yes, it landed" cue from
 *     Omar's spec.
 *   • Idle: not rendered.
 *
 * `prefers-reduced-motion` collapses the indeterminate animation to a
 * static gradient and the fade-out to an instant hide.
 */

import { useEffect, useRef, useState } from "react";
import { useProgressStore } from "@/stores/progressStore";

const LINGER_MS = 500;
const FADE_MS = 200;

type Phase = "idle" | "active" | "completing";

export function TopProgressBar() {
  const activeOps = useProgressStore((s) => s.activeOps);
  const [phase, setPhase] = useState<Phase>("idle");
  const fadeTimer = useRef<number | null>(null);
  const clearTimer = useRef<number | null>(null);

  useEffect(() => {
    if (activeOps > 0) {
      // Cancel any in-flight linger / fade if a new op kicks off
      // before the old one finished hiding.
      if (fadeTimer.current) window.clearTimeout(fadeTimer.current);
      if (clearTimer.current) window.clearTimeout(clearTimer.current);
      setPhase("active");
      return;
    }
    // activeOps just dropped to 0. Only transition into the linger
    // if we were active; idle → idle is a no-op.
    if (phase === "active") {
      setPhase("completing");
      fadeTimer.current = window.setTimeout(() => {
        // Hide after LINGER_MS by setting phase back to idle, but
        // give CSS the FADE_MS window to opacity-fade the element
        // before it's removed from the DOM.
        clearTimer.current = window.setTimeout(() => {
          setPhase("idle");
        }, FADE_MS);
      }, LINGER_MS);
    }
    return () => {
      if (fadeTimer.current) window.clearTimeout(fadeTimer.current);
      if (clearTimer.current) window.clearTimeout(clearTimer.current);
    };
    // We intentionally don't depend on `phase` here — the effect
    // reads it via closure on each activeOps change, and the only
    // valid transitions are activeOps-driven.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeOps]);

  if (phase === "idle") return null;

  const isActive = phase === "active";
  return (
    <div
      role="progressbar"
      aria-busy={isActive}
      className={`fixed inset-x-0 top-0 z-50 h-1 overflow-hidden transition-opacity ${
        phase === "completing" ? "opacity-0" : "opacity-100"
      }`}
      style={{ transitionDuration: `${FADE_MS}ms` }}
    >
      <div
        className={`h-full ${
          isActive
            ? "bg-gradient-to-r from-pulse/40 via-pulse to-pulse/40 dark:from-pulse-dark/40 dark:via-pulse-dark dark:to-pulse-dark/40 motion-safe:animate-progress-indeterminate"
            : "bg-approval dark:bg-approval-dark"
        }`}
      />
    </div>
  );
}
